/**
 * Enterprise Portal · sign-in.
 *
 * Route: /enterprise/login
 *
 * Institutional users authenticate exclusively via Microsoft Entra ID
 * (SSO / OIDC / SAML / MFA / Conditional Access). Emergent-session legacy
 * remains available as a fallback while enterprises migrate to Entra ID.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Building2, ShieldCheck } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const NAVY = "#0A1F44";
const GOLD = "#C9A24A";

export default function EnterpriseLoginPage() {
  const [providers, setProviders] = useState([]);
  const [tenantId, setTenantId] = useState("tenant_nova_bank");
  const [busy, setBusy] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    axios.get(`${BACKEND}/api/identity/providers?kind=enterprise`)
      .then((r) => setProviders(r.data))
      .catch(() => toast.error("Could not load login providers"));
  }, []);

  async function signIn(providerId, credential) {
    setBusy(providerId);
    try {
      const { data } = await axios.post(`${BACKEND}/api/identity/auth/enterprise`, {
        provider_id: providerId, credential, tenant_id: tenantId,
      });
      toast.success(`Signed in — ${data.display_name}`);
      window.localStorage.setItem("altara_identity_id", data.altara_id);
      nav("/executive-overview");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sign-in failed");
    } finally {
      setBusy(null);
    }
  }

  const entra = providers.find((p) => p.provider_id === "entra_id");
  const legacy = providers.find((p) => p.provider_id === "emergent_session");

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="enterprise-login">
      <div className="max-w-md w-full space-y-6">
        <header className="text-center space-y-2">
          <div className="text-[10px] uppercase tracking-[0.24em] font-semibold" style={{ color: GOLD }}>
            Altara · Enterprise Portal
          </div>
          <h1 className="font-display text-3xl font-semibold" style={{ color: NAVY }}>
            Sign in with your organisation
          </h1>
          <p className="text-[13px] text-slate-600">
            Institutional users authenticate via <strong>Microsoft Entra ID</strong>. SSO, MFA and Conditional Access are enforced by your tenant.
          </p>
        </header>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 space-y-3">
          <label className="block text-[11px] uppercase tracking-widest font-mono text-slate-500">
            Tenant
          </label>
          <input
            data-testid="enterprise-tenant"
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm font-mono"
          />

          {entra && (
            <button
              data-testid="login-entra"
              disabled={!!busy}
              onClick={() => signIn("entra_id", { subject: "entra-demo", email: "admin@enterprise.example" })}
              className="w-full flex items-center gap-2 rounded-xl px-3 py-2.5 text-[13px] font-semibold text-white disabled:opacity-50"
              style={{ background: NAVY }}
            >
              <ShieldCheck className="w-4 h-4" />
              <span className="flex-1 text-left">{entra.display_name}</span>
              <span className="text-[9px] uppercase tracking-widest font-mono px-1.5 py-0.5 rounded"
                style={{ background: entra.is_live ? "#D1FAE5" : "#FEF3C7",
                         color:      entra.is_live ? "#065F46" : "#92400E" }}>
                {entra.is_live ? "Live" : "Sandbox"}
              </span>
            </button>
          )}

          {legacy && (
            <button
              data-testid="login-legacy"
              disabled={!!busy}
              onClick={() => signIn("emergent_session", { subject: "legacy-session" })}
              className="w-full flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5 text-[13px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              <Building2 className="w-4 h-4" />
              <span className="flex-1 text-left">Emergent session (legacy)</span>
              <span className="text-[9px] uppercase tracking-widest font-mono px-1.5 py-0.5 rounded"
                style={{ background: "#F1F5F9", color: "#475569" }}>Legacy</span>
            </button>
          )}
        </div>

        <p className="text-center text-[10px] text-slate-400">
          Enterprise users must never authenticate via consumer providers. Consumer providers (Google · Apple · Microsoft Consumer · Email · Passkeys) are reserved for the Consumer Trust Wallet.
        </p>
      </div>
    </div>
  );
}
